import * as stringSimilarity from "string-similarity";
import SLLogo from "./icons/srilanka.svg";
import QatarLogo from "./icons/qutar.svg";
import SaudiLogo from "./icons/saudi.svg";
import IndiaLogo from "./icons/india.svg";
import KuwaitLogo from "./icons/kuwait.svg";
import DubaiLogo from "./icons/dubai.svg";

export const nth = (n: number) => {
  return ["st", "nd", "rd"][((((n + 90) % 100) - 10) % 10) - 1] || "th";
};

export const formatCurrency = (value: any) => {
  return !isNaN(value) && value !== undefined
    ? value.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,")
    : "";
};

export const CurrencyFormatter = ({
  amount,
  fractionDigits = 2,
  currencyCode,
}: any) => {
  if (amount === undefined || amount === "") return "";

  // Convert to string and separate the minus sign if present
  const isNegative = amount < 0;
  const absoluteAmount = Math.abs(amount).toString();

  // Remove any commas, replace dots, and pad to required length
  const paddedNumber = absoluteAmount
    .replace(".", "")
    .replace(/,/g, "")
    .padStart(4, "0")
    .split("");

  // Set fraction digits for specific currencies
  if (["YEN"].includes(currencyCode)) fractionDigits = 0;

  // Insert decimal point
  paddedNumber.splice(paddedNumber.length - fractionDigits, 0, ".");

  // Format the number and add back minus sign if needed
  const formattedAmount = parseFloat(paddedNumber.join("")).toLocaleString(
    "en-US",
    {
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    },
  );

  return isNegative ? `-${formattedAmount}` : formattedAmount;
};

export const captialCase = (name: string) => {
  if (name && name.split("_").length > 1)
    return name
      .split("_")
      .reduce(
        (a, b) =>
          a[0].toUpperCase() +
          "" +
          a.slice(1) +
          " " +
          b[0].toUpperCase() +
          "" +
          b.slice(1),
      );
  else return name[0].toUpperCase() + "" + name.slice(1);
};

export const findEquivalentName = (
  userInput: string,
  nameArray: Array<string>,
) => {
  const threshold = 0.4; // Adjust the threshold based on your requirements
  let closestMatch = null;
  let highestSimilarity = 0;

  if (userInput && nameArray.length) {
    nameArray.forEach((name) => {
      const similarity = stringSimilarity.compareTwoStrings(
        userInput.toLowerCase(),
        name.toLowerCase(),
      );

      if (similarity > highestSimilarity && similarity >= threshold) {
        closestMatch = name;
        highestSimilarity = similarity;
      }
    });
  }

  return closestMatch;
};

export const equationToArray = (equation: string) => {
  const operators = ["+", "-", "*", "/", "(", ")"];
  const tokens = [];
  let currentToken = "";

  for (let i = 0; i < equation.length; i++) {
    const char = equation[i];

    if (char === " ") {
      continue;
    }

    if (operators.includes(char)) {
      if (currentToken !== "") {
        tokens.push(currentToken.trim());
        currentToken = "";
      }
      tokens.push(char);
    } else {
      currentToken += char;
    }
  }

  if (currentToken !== "") {
    tokens.push(currentToken.trim());
  }

  return tokens;
};

export const arrayToEquation = (tokens: any) => {
  let equation = "";
  let previousType = "";

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    if (token.type === "operator" || token.type === "numeric") {
      equation += token.value;
    } else if (token.type === "metric") {
      if (previousType === "metric") {
        equation += " ";
      }
      equation += token.value;
    }

    previousType = token.type;
  }

  return equation;
};

export const convertToInternationalCurrencySystem = (
  labelValue: string,
  roundOff: number = 2,
) => {
  const numericValue = parseFloat(labelValue);

  // Nine Zeroes for Billions
  if (Math.abs(numericValue) >= 1.0e9) {
    return (numericValue / 1.0e9).toFixed(roundOff) + "B";
  }
  // Six Zeroes for Millions
  else if (Math.abs(numericValue) >= 1.0e6) {
    return (numericValue / 1.0e6).toFixed(roundOff) + "M";
  }
  // Three Zeroes for Thousands
  else if (Math.abs(numericValue) >= 1.0e3) {
    return (numericValue / 1.0e3).toFixed(roundOff) + "K";
  }

  // Handle negative values
  return numericValue.toFixed(roundOff).toString();
};

export const graphColors = [
  "#00CC99",
  "#009999",
  "#0099CC",
  "#33CCCC",
  "#3399FF",
  "#0066CC",
  "#66FFFF",
  "#66CCFF",
];

const shuffleArray = (array: string[]) => {
  const shuffledArray: string[] = [];
  const usedColors = new Set<string>();

  while (shuffledArray.length < array.length) {
    const randomIndex = Math.floor(Math.random() * array.length);
    const color = array[randomIndex];

    if (!usedColors.has(color)) {
      shuffledArray.push(color);
      usedColors.add(color);
    }
  }

  return shuffledArray;
};

export const getRandomGraphColor = () => {
  const shuffledColors = shuffleArray([...graphColors]); // Shuffle a copy of the array
  return shuffledColors;
};

export const currencyDecimalMap: any = {
  INR: 2,
  LKR: 2,
  USD: 4,
  QAR: 4,
  AED: 2,
  SAR: 2,
  BHD: 3,
  KWD: 2,
};

export const STOCK_EXCHANGES = [
  "Colombo Stock Exchange",
  "Saudi Stock Exchange",
  "Qatar Stock Exchange",
  "Kuwait Stock Exchange",
  "Indian Stock Exchange",
  "Dubai Financial Market",
  "Abu Dhabi Securities Exchange",
];

export const getExchangeInfo = (exchangeName: string) => {
  switch (exchangeName) {
    case "Colombo Stock Exchange":
      return { logo: SLLogo, short: "CSE" };
    case "Qatar Stock Exchange":
      return { logo: QatarLogo, short: "QSE" };
    case "Saudi Stock Exchange":
      return { logo: SaudiLogo, short: "SSE" };
    case "Kuwait Stock Exchange":
      return { logo: KuwaitLogo, short: "KSE" };
    case "Indian Stock Exchange":
      return { logo: IndiaLogo, short: "ISE" };
    case "Dubai Financial Market":
      return { logo: DubaiLogo, short: "DFM" };
    case "Abu Dhabi Securities Exchange":
      return { logo: DubaiLogo, short: "ADX" };
    default:
      return { logo: null, short: exchangeName || "N/A" };
  }
};

export const marketCurrencyMap: any = {
  CSE: "LKR",
  QSE: "QAR",
  SSE: "SAR",
  KSE: "KWD",
  ISE: "INR",
  DFM: "AED",
  ADX: "AED",
};

export const marketLogoMap: any = {
  "Colombo Stock Exchange": SLLogo,
  "Qatar Stock Exchange": QatarLogo,
  "Saudi Stock Exchange": SaudiLogo,
  "Kuwait Stock Exchange": KuwaitLogo,
  "Indian Stock Exchange": IndiaLogo,
  "Dubai Financial Market": DubaiLogo,
  "Abu Dhabi Securities Exchange": DubaiLogo,
};

export function flattenMetrics(metricsDataParam: any) {
  const flatMetrics: any[] = [];
  Object.keys(metricsDataParam).forEach((categoryKey) => {
    const categoryMetrics = metricsDataParam[categoryKey];
    if (Array.isArray(categoryMetrics)) {
      categoryMetrics.forEach((metric: any, index: number) => {
        const metricObject = {
          ...metric,
          sortableId: metric.sortableId || `${categoryKey}-${index}`,
        };
        flatMetrics.push(metricObject);
      });
    }
  });
  return flatMetrics;
}
